import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  isInvoiceClubVisible,
  type DocumentDto,
  type InvoiceDto,
  type InvoiceStatus,
  type TripDto,
} from "@tc/shared";
import {
  type BadgeStatus,
  Button,
  Card,
  CardHead,
  CardTitle,
  DocumentRow,
  Icon,
  Modal,
  StatusBadge,
  Toast,
  VisibilityTag,
} from "@/components/ui";
import { Can, useAuthz } from "@/lib/auth/authz";
import { useFormatters } from "@/lib/i18n/format";
import { downloadDocument, recordsApi, runRecordPdf, uploadTripDocument } from "./api";

const INVOICE_STATUS_BADGE: Record<InvoiceStatus, BadgeStatus> = {
  Draft: "enquiry",
  Issued: "proposal",
  Paid: "confirmed",
};

const INVOICE_STATUS_KEY: Record<InvoiceStatus, string> = {
  Draft: "invoice.draft",
  Issued: "records.issued",
  Paid: "records.paid",
};

/**
 * Documents tab (TW-3450) — Invoices and Documents render as separate sections on a trip. Back office
 * authors the invoice lifecycle (create a Draft prefilled from the proposal total, edit/delete while
 * Draft, "issue to club" to flip it visible, "mark as paid") and uploads documents; the club is
 * read-only and — by the invoice RLS Draft-hiding predicate — only ever receives Issued/Paid
 * invoices. Each invoice carries an eye / eye-off visibility tag. The branded invoice PDF is TW-3451.
 */
export function DocumentsTab({ trip }: { trip: TripDto }) {
  const { t } = useTranslation();
  const { isBackOffice } = useAuthz();

  const [invoices, setInvoices] = useState<InvoiceDto[] | null>(null);
  const [documents, setDocuments] = useState<DocumentDto[] | null>(null);
  const [error, setError] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  }, []);

  const load = useCallback(async () => {
    setError(false);
    try {
      const [inv, docs] = await Promise.all([
        recordsApi.listInvoices(trip.id),
        recordsApi.listDocuments(trip.id),
      ]);
      setInvoices(inv);
      setDocuments(docs);
    } catch {
      setError(true);
    }
  }, [trip.id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Card><div className="empty">{t("records.loadError")}</div></Card>;
  if (invoices === null || documents === null) {
    return <Card><div className="empty">{t("common.loading")}</div></Card>;
  }

  return (
    <div className="stack">
      <DocumentsSection
        trip={trip}
        documents={documents}
        canManage={isBackOffice}
        onChanged={load}
        flash={flash}
      />
      <InvoicesSection
        trip={trip}
        invoices={invoices}
        canManage={isBackOffice}
        onChanged={load}
        flash={flash}
      />
      {toast && <Toast message={toast} />}
    </div>
  );
}

/**
 * Just the Documents section, self-loading — for state-specific one-page views (e.g. the club's
 * ProposalSent page) that show documents without the invoice ledger. Both audiences can upload
 * (shared `document.write`); deletion stays back-office only (`canManage`).
 */
export function DocumentsPanel({ trip }: { trip: TripDto }) {
  const { t } = useTranslation();
  const { isBackOffice } = useAuthz();
  const [documents, setDocuments] = useState<DocumentDto[] | null>(null);
  const [error, setError] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  }, []);

  const load = useCallback(async () => {
    setError(false);
    try {
      setDocuments(await recordsApi.listDocuments(trip.id));
    } catch {
      setError(true);
    }
  }, [trip.id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Card><div className="empty">{t("records.loadError")}</div></Card>;
  if (documents === null) return <Card><div className="empty">{t("common.loading")}</div></Card>;

  return (
    <>
      <DocumentsSection trip={trip} documents={documents} canManage={isBackOffice} onChanged={load} flash={flash} />
      {toast && <Toast message={toast} />}
    </>
  );
}

// ── Invoices ──────────────────────────────────────────────────────────────────

function InvoicesSection({
  trip,
  invoices,
  canManage,
  onChanged,
  flash,
}: {
  trip: TripDto;
  invoices: InvoiceDto[];
  canManage: boolean;
  onChanged: () => Promise<void>;
  flash: (msg: string) => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const navigate = useNavigate();
  const [issuing, setIssuing] = useState<InvoiceDto | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pdfId, setPdfId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  /** Create a Draft (auto-seeded from the accepted proposal) and open the factura page to edit it. */
  const createAndOpen = useCallback(async () => {
    setCreating(true);
    try {
      const created = await recordsApi.createInvoice(trip.id, {});
      navigate(`/trips/${trip.id}/invoices/${created.id}`);
    } catch {
      flash(t("records.saveError"));
      setCreating(false);
    }
  }, [trip.id, navigate, flash, t]);

  const downloadPdf = useCallback(
    async (inv: InvoiceDto) => {
      setPdfId(inv.id);
      try {
        await runRecordPdf(
          () => recordsApi.requestInvoicePdf(trip.id, inv.id),
          (exportId) => recordsApi.invoicePdfStatus(trip.id, inv.id, exportId),
        );
      } catch {
        flash(t("records.pdfError"));
      } finally {
        setPdfId(null);
      }
    },
    [trip.id, flash, t],
  );

  const run = useCallback(
    async (id: string, op: () => Promise<unknown>, ok: string) => {
      setBusyId(id);
      try {
        await op();
        await onChanged();
        flash(t(ok));
      } catch {
        flash(t("records.saveError"));
      } finally {
        setBusyId(null);
      }
    },
    [onChanged, flash, t],
  );

  return (
    <Card>
      <CardHead>
        <CardTitle>{t("event.invoicesLabel")}</CardTitle>
        {canManage && (
          <Button size="sm" icon="plus" onClick={createAndOpen} disabled={creating}>
            {t("invoice.create")}
          </Button>
        )}
      </CardHead>

      {invoices.length === 0 ? (
        <div className="empty" style={{ marginTop: 12 }}>{t("invoice.none")}</div>
      ) : (
        <div className="rec-list" style={{ marginTop: 12 }}>
          {invoices.map((inv) => (
            <div key={inv.id} className="rec-row">
              <div className="rec-main">
                <div className="row" style={{ gap: 8, alignItems: "center" }}>
                  <span className="mono">{inv.number}</span>
                  <StatusBadge
                    status={INVOICE_STATUS_BADGE[inv.status]}
                    label={t(INVOICE_STATUS_KEY[inv.status])}
                  />
                </div>
                <div className="row" style={{ gap: 10, alignItems: "center", marginTop: 4 }}>
                  <strong>{fmt.currency(inv.amount, inv.currency)}</strong>
                  {canManage && (
                    <VisibilityTag
                      visible={isInvoiceClubVisible(inv.status)}
                      shownLabel={t("invoice.visible")}
                      hiddenLabel={t("invoice.notVisible")}
                    />
                  )}
                </div>
              </div>
              <div className="rec-actions">
                <Button
                  size="sm"
                  variant="ghost"
                  iconRight="arrowR"
                  onClick={() => navigate(`/trips/${trip.id}/invoices/${inv.id}`)}
                >
                  {t("factura.open")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  icon="download"
                  disabled={pdfId === inv.id}
                  onClick={() => downloadPdf(inv)}
                >
                  {pdfId === inv.id
                    ? t("records.generatingPdf")
                    : inv.status === "Draft"
                      ? t("factura.downloadProforma")
                      : t("records.downloadPdf")}
                </Button>
                {canManage && inv.status === "Draft" && (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon="x"
                      disabled={busyId === inv.id}
                      onClick={() => run(inv.id, () => recordsApi.deleteInvoice(trip.id, inv.id), "invoice.deleted")}
                    >
                      {t("invoice.delete")}
                    </Button>
                    <Button size="sm" icon="send" onClick={() => setIssuing(inv)}>
                      {t("invoice.issueToClub")}
                    </Button>
                  </>
                )}
                {canManage && inv.status === "Issued" && (
                  <Button
                    size="sm"
                    icon="check"
                    disabled={busyId === inv.id}
                    onClick={() => run(inv.id, () => recordsApi.payInvoice(trip.id, inv.id), "records.paidToast")}
                  >
                    {t("invoice.markPaid")}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {issuing && (
        <Modal open onClose={() => setIssuing(null)} title={t("invoice.issueTitle")}>
          <div className="modal-body">
            <p className="sub">{t("invoice.issueDesc")}</p>
          </div>
          <div className="modal-foot">
            <Button variant="ghost" onClick={() => setIssuing(null)}>
              {t("actions.cancel")}
            </Button>
            <Button
              icon="send"
              disabled={busyId === issuing.id}
              onClick={async () => {
                const inv = issuing;
                setIssuing(null);
                await run(inv.id, () => recordsApi.issueInvoice(trip.id, inv.id), "invoice.issuedToast");
              }}
            >
              {t("invoice.issue")}
            </Button>
          </div>
        </Modal>
      )}
    </Card>
  );
}

// ── Documents ───────────────────────────────────────────────────────────────

/** The uppercase file extension shown in the meta line (e.g. "PDF", "XLSX"). */
function fileExt(filename: string): string {
  const i = filename.lastIndexOf(".");
  return i >= 0 && i < filename.length - 1 ? filename.slice(i + 1).toUpperCase() : "FILE";
}

/** A soft background + icon colour tinted by file type — PDFs red, spreadsheets green, images blue. */
function fileVisual(filename: string): { bg: string; fg: string } {
  const ext = fileExt(filename).toLowerCase();
  if (ext === "pdf") return { bg: "var(--danger-soft)", fg: "var(--danger)" };
  if (["xlsx", "xls", "csv"].includes(ext)) return { bg: "var(--success-soft)", fg: "var(--success)" };
  if (["png", "jpg", "jpeg", "gif", "webp", "heic", "svg"].includes(ext)) return { bg: "var(--info-soft)", fg: "var(--info)" };
  return { bg: "var(--surface-3)", fg: "var(--text-faint)" };
}

/** Human-readable file size — MB to one decimal once past a megabyte, otherwise whole KB. */
function fileSizeLabel(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function DocumentsSection({
  trip,
  documents,
  canManage,
  onChanged,
  flash,
}: {
  trip: TripDto;
  documents: DocumentDto[];
  canManage: boolean;
  onChanged: () => Promise<void>;
  flash: (msg: string) => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setUploading(true);
    try {
      await uploadTripDocument(trip.id, file);
      await onChanged();
    } catch {
      flash(t("records.uploadError"));
    } finally {
      setUploading(false);
    }
  }

  return (
    <Card>
      <CardHead>
        <div>
          <CardTitle>{t("event.documentsLabel")}</CardTitle>
          <div className="faint" style={{ fontSize: 12.5, marginTop: 2 }}>
            {t("records.docCount", { n: documents.length })}
          </div>
        </div>
        {/* Uploading is open to both audiences now (shared `document.write`) — a club attaches its own
            paperwork. Deletion remains back-office only (gated by `canManage` on each row). */}
        <Can permission="document.write">
          <input ref={fileRef} type="file" hidden onChange={onPick} />
          <Button
            variant="secondary"
            size="sm"
            icon="upload"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
          >
            {uploading ? t("records.uploading") : t("event.uploadDoc")}
          </Button>
        </Can>
      </CardHead>

      {documents.length === 0 ? (
        <div className="empty" style={{ marginTop: 12 }}>{t("records.noDocuments")}</div>
      ) : (
        <div className="doc-list" style={{ marginTop: 6 }}>
          {documents.map((doc) => {
            const visual = fileVisual(doc.filename);
            const fromTc = doc.uploaderIsBackOffice;
            // TC uploads read as the org ("Travel Connection") — never the BO person's name; a club
            // upload shows the colleague who added it. No chip if the uploader can't be resolved.
            const byLabel = fromTc ? t("records.uploaderTc") : doc.uploaderName;
            return (
              <DocumentRow
                key={doc.id}
                name={doc.filename}
                meta={`${fileExt(doc.filename)} · ${fileSizeLabel(doc.sizeBytes)} · ${fmt.date(doc.createdAt)}`}
                accentColor={fromTc ? "var(--primary)" : "var(--info)"}
                fileColor={visual}
                uploader={byLabel ? { label: byLabel, isBackOffice: fromTc } : null}
                actions={
                  <>
                    <button
                      type="button"
                      className="doc-icon-btn"
                      aria-label={t("records.download")}
                      onClick={() => downloadDocument(trip.id, doc)}
                    >
                      <Icon name="download" size={16} />
                    </button>
                    {canManage && (
                      <button
                        type="button"
                        className="doc-icon-btn"
                        aria-label={t("records.deleteDoc")}
                        disabled={busyId === doc.id}
                        onClick={async () => {
                          setBusyId(doc.id);
                          try {
                            await recordsApi.deleteDocument(trip.id, doc.id);
                            await onChanged();
                          } catch {
                            flash(t("records.saveError"));
                          } finally {
                            setBusyId(null);
                          }
                        }}
                      >
                        <Icon name="x" size={16} />
                      </button>
                    )}
                  </>
                }
              />
            );
          })}
        </div>
      )}
    </Card>
  );
}
